import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

import EcoIcon from "../../../components/eco-icon/EcoIcon";
import EcoPageHeader from "../../../components/eco-page-header/EcoPageHeader";
import EcoSegmented from "../../../components/eco-ui/EcoSegmented";
import {
  budgetLabel,
  fmtDate,
  fmtInt,
  fmtMoney,
  listCampaignsApi,
  STATUS_LABEL,
  STATUS_PILL,
  type CampaignListItem,
  type CampaignStatus,
} from "../campanhasApi";
import AiAssistantDrawer from "../components/AiAssistantDrawer";
import ApplicationsPanel from "../components/ApplicationsPanel";
import { CAMPANHA_ASSISTENTE_PAGE, CAMPANHA_NEW_PAGE, CAMPANHA_PAGE, CRIATIVOS_PAGE } from "../routes";
import "./Campanhas.css";

type Tab = "campanhas" | "candidatos";

const TABS: { value: Tab; label: string }[] = [
  { value: "campanhas", label: "Campanhas" },
  { value: "candidatos", label: "Todos os candidatos" },
];

const FILTERS: { value: CampaignStatus | ""; label: string }[] = [
  { value: "", label: "Todas" },
  { value: "ATIVA", label: "Ativas" },
  { value: "PAUSADA", label: "Pausadas" },
  { value: "RASCUNHO", label: "Rascunhos" },
  { value: "ENCERRADA", label: "Encerradas" },
];

const period = (c: { startDate: string; endDate: string | null }) =>
  c.endDate ? `${fmtDate(c.startDate)} a ${fmtDate(c.endDate)}` : `desde ${fmtDate(c.startDate)}`;

/** Campanhas de captação de freelancer na Meta e todos os candidatos. */
const CampanhasPage: React.FC = () => {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab: Tab = params.get("aba") === "candidatos" ? "candidatos" : "campanhas";
  const [status, setStatus] = useState<CampaignStatus | "">("");
  const [campaigns, setCampaigns] = useState<CampaignListItem[] | null>(null);

  useEffect(() => {
    setCampaigns(null);
    listCampaignsApi(status || undefined)
      .then(setCampaigns)
      .catch((e) => {
        toast.error(e?.message);
        setCampaigns([]);
      });
  }, [status]);

  const renderCampaigns = () => {
    if (!campaigns) return <p className="cmpLoading">Carregando...</p>;
    if (!campaigns.length) {
      return (
        <div className="cmpPanel__pad">
          <p className="ecoEmpty">
            {status ? "Nenhuma campanha com esse status." : 'Nenhuma campanha ainda. Clique em "Nova campanha" para montar a primeira.'}
          </p>
        </div>
      );
    }
    return (
      <>
        <div className="ecoTableWrap cmpDesktop">
          <table className="ecoTable">
            <thead>
              <tr>
                <th scope="col">Campanha</th>
                <th scope="col">Status</th>
                <th scope="col">Período</th>
                <th scope="col">Orçamento</th>
                <th scope="col" className="ecoTable__num">Gasto</th>
                <th scope="col" className="ecoTable__num">Cliques</th>
                <th scope="col" className="ecoTable__num">Candidatos</th>
                <th scope="col" className="ecoTable__num">Custo por candidato</th>
              </tr>
            </thead>
            <tbody>
              {campaigns.map((c) => (
                <tr key={c.id} className="cmpRow" onClick={() => navigate(CAMPANHA_PAGE(c.id))}>
                  <td>
                    <button type="button" className="ecoLinkBtn cmpStrong" onClick={() => navigate(CAMPANHA_PAGE(c.id))}>
                      {c.name}
                    </button>
                    <span className="cmpSub">{c.jobTitle}</span>
                  </td>
                  <td>
                    <span className={STATUS_PILL[c.status]}>{STATUS_LABEL[c.status]}</span>
                  </td>
                  <td className="ecoTable__muted">{period(c)}</td>
                  <td className="ecoTable__muted">{budgetLabel(c.budgetType, c.budgetCents)}</td>
                  <td className="ecoTable__num">{fmtMoney(c.spendCents)}</td>
                  <td className="ecoTable__num">{fmtInt(c.linkClicks)}</td>
                  <td className="ecoTable__num">{fmtInt(c.applications)}</td>
                  <td className="ecoTable__num">{fmtMoney(c.costPerApplicationCents)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <ul className="cmpCards cmpMobile">
          {campaigns.map((c) => (
            <li key={c.id}>
              <button type="button" className="cmpCard" onClick={() => navigate(CAMPANHA_PAGE(c.id))}>
                <span className="cmpCard__top">
                  <span>
                    <span className="cmpCard__title">{c.name}</span>
                    <span className="cmpSub">{period(c)}</span>
                  </span>
                  <span className={STATUS_PILL[c.status]}>{STATUS_LABEL[c.status]}</span>
                </span>
                <span className="cmpCard__meta">
                  {fmtMoney(c.spendCents)} gastos · {fmtInt(c.applications)} candidatos · {fmtMoney(c.costPerApplicationCents)} por candidato
                </span>
              </button>
            </li>
          ))}
        </ul>
      </>
    );
  };

  return (
    <div className="cmpPage">
      <main className="cmpPage__main">
        <EcoPageHeader
          crumbs={[{ label: "Início", onClick: () => navigate("/") }, { label: "Campanhas" }]}
          title="Campanhas"
          subtitle="Captação de freelancers no Facebook e no Instagram. Cada campanha tem a sua página de candidatura, e os candidatos ficam guardados aqui."
          actions={
            <>
              <button type="button" className="ecoBtn" onClick={() => navigate(CRIATIVOS_PAGE())}>
                <EcoIcon name="camera" size={18} />
                Criativos
              </button>
              <button type="button" className="ecoBtn" onClick={() => navigate(CAMPANHA_NEW_PAGE())}>
                <EcoIcon name="plus" size={18} />
                Nova campanha
              </button>
              <button type="button" className="ecoBtn ecoBtn--primary" onClick={() => navigate(CAMPANHA_ASSISTENTE_PAGE())}>
                <EcoIcon name="helpCircle" size={18} />
                Criar com a Eco-IA
              </button>
            </>
          }
        />

        <section className="ecoCard cmpPanel cmpPanel--flush">
          <div className="cmpPanel__head">
            <EcoSegmented label="Mostrar" options={TABS} value={tab} onChange={(v) => setParams(v === "candidatos" ? { aba: v } : {}, { replace: true })} />
            {tab === "campanhas" ? (
              <EcoSegmented label="Status" options={FILTERS} value={status} onChange={setStatus} />
            ) : null}
          </div>
          {tab === "campanhas" ? renderCampaigns() : <ApplicationsPanel />}
        </section>
      </main>
      <AiAssistantDrawer />
    </div>
  );
};

export default CampanhasPage;
